import { Card, Message, Pagination, Tabs, Typography } from "@arco-design/web-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  notificationCenterDefaultPageSize,
  notificationViewLabel,
  resolveSafeNotificationTarget,
  useMarkNotificationMessageReadMutation,
  useNotificationMessagesQuery,
  useNotificationMessageSummaryQuery,
} from "../runtime";
import type { NotificationMessage, NotificationMessageView } from "../runtime";
import { NotificationDetailDrawer, NotificationMessageList } from "../ui";
import "../ui/NotificationCenter.css";

/**
 * 当前登录用户的完整消息中心页面。
 */
export function NotificationCenterPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<NotificationMessageView>("action");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(notificationCenterDefaultPageSize);
  const [detail, setDetail] = useState<NotificationMessage | null>(null);
  const summaryQuery = useNotificationMessageSummaryQuery();
  const messagesQuery = useNotificationMessagesQuery({ view, page, pageSize });
  const markRead = useMarkNotificationMessageReadMutation();

  const markReadWithoutBlocking = async (messageId: string) => {
    try {
      await markRead.mutateAsync(messageId);
    } catch {
      // 已读接口失败不阻断普通详情或真实业务入口。
    }
  };

  const openNotice = async (message: NotificationMessage) => {
    setDetail(message);
    await markReadWithoutBlocking(message.id);
  };

  const openAction = async (message: NotificationMessage) => {
    const target = resolveSafeNotificationTarget(message.targetUrl);
    if (!target) {
      Message.error("消息跳转地址无效，请联系消息来源功能负责人。");
      return;
    }
    navigate(target);
    void markReadWithoutBlocking(message.id);
  };

  return (
    <main className="notification-center-page">
      <header className="notification-center-page__header">
        <div>
          <Typography.Title heading={4} style={{ margin: 0 }}>通知公告</Typography.Title>
          <Typography.Text type="secondary">集中查看普通通知和仍需进入业务页面处理的站内消息。</Typography.Text>
        </div>
        <div className="notification-center-page__summary" aria-label="消息数量汇总">
          <span>未读 <strong>{summaryQuery.data?.unreadCount ?? 0}</strong></span>
          <span>待处理 <strong>{summaryQuery.data?.openActionCount ?? 0}</strong></span>
          <span>未读通知 <strong>{summaryQuery.data?.unreadNoticeCount ?? 0}</strong></span>
        </div>
      </header>

      <Card className="notification-center-page__card">
        <Tabs
          activeTab={view}
          onChange={(key) => {
            setView(key as NotificationMessageView);
            setPage(1);
          }}
        >
          {(["action", "notice", "all"] as const).map((item) => (
            <Tabs.TabPane key={item} title={notificationViewLabel(item)} />
          ))}
        </Tabs>
        <NotificationMessageList
          items={messagesQuery.data?.items ?? []}
          loading={messagesQuery.isLoading}
          error={messagesQuery.isError}
          onRetry={() => void messagesQuery.refetch()}
          onOpenNotice={(message) => void openNotice(message)}
          onOpenAction={(message) => void openAction(message)}
        />
        {messagesQuery.data && messagesQuery.data.total > 0 ? (
          <div className="notification-center-page__pagination">
            <Pagination
              current={page}
              pageSize={pageSize}
              total={messagesQuery.data.total}
              sizeCanChange
              sizeOptions={[10, 20, 50]}
              showTotal
              onChange={(nextPage, nextPageSize) => {
                if (nextPageSize !== pageSize) {
                  setPageSize(nextPageSize);
                  setPage(1);
                  return;
                }
                setPage(nextPage);
              }}
            />
          </div>
        ) : null}
      </Card>
      <NotificationDetailDrawer message={detail} onClose={() => setDetail(null)} />
    </main>
  );
}
